import { useMutation } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import type { AxiosError } from "axios"
import { authApi } from "@/features/auth/api/auth.api"
import { landingFor, safeReturnPath } from "@/features/auth/landing"
import { useAuthStore } from "@/store/auth.store"
import type { ProblemDetail } from "@/lib/api-client"
import type { LoginRequest } from "@/types/auth"

/** The message shown for a failed sign-in. The API never says which part was wrong. */
export function loginErrorMessage(err: AxiosError<ProblemDetail> | null): string | null {
  if (!err) return null
  const status = err.response?.status
  if (status === 401) return "Invalid username or password."
  if (status === 429) return err.response?.data?.detail ?? "Too many sign-in attempts. Please wait a few minutes and try again."
  if (status === 422) return "Enter your username and password."
  return "The platform could not be reached. Please try again."
}

export function useAuth() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const setMe = useAuthStore((s) => s.setMe)
  const me = useAuthStore((s) => s.me)

  const loginMutation = useMutation<Awaited<ReturnType<typeof authApi.login>>, AxiosError<ProblemDetail>, LoginRequest>({
    mutationFn: (data) => authApi.login(data),
    onSuccess: (session) => {
      setMe(session)
      navigate(safeReturnPath(params.get("from"), session) ?? landingFor(session), { replace: true })
    },
  })

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      useAuthStore.getState().clear()
      // A full page load clears every in-memory dataset and cache from this session
      window.location.replace("/auth/login?reason=signed-out")
    },
  })

  return {
    me,
    login: loginMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    loginError: loginErrorMessage(loginMutation.error),
    logout: () => logoutMutation.mutate(),
    isLoggingOut: logoutMutation.isPending,
  }
}
